import React from "react";
import classNames from "classnames";
import CiroButton, { CiroButtonStyleEnum } from "../../../shared/CiroButton";
import CiroTable from "../../../shared/CiroTable/CiroTable";
import CiroTableCell from "../../../shared/CiroTable/CiroTableCell";
import CiroTableRow from "../../../shared/CiroTable/CiroTableRow";
import CiroTableHeader from "../../../shared/CiroTable/CiroTableHeader";
import {
  ContactInvalidReasonsEnum,
  IContactInput,
  IImportState,
  ImportStepEnum,
} from "./useImportState";
import { CheckIcon, XCircleIcon } from "@heroicons/react/24/outline";
import CiroTooltipContainer from "../../../shared/CiroTooltipContainer";

interface IValidationStepProps {
  importState: IImportState;
  contactListId: number;
}

const ValidationStep: React.FC<IValidationStepProps> = ({
  importState,
  contactListId,
}) => {
  const {
    setStep,
    csvData,
    mappings,
    setError,
    isProcessing,
    setIsProcessing,
    validatedContacts,
    setValidatedContacts,
    showOnlyInvalid,
    setShowOnlyInvalid,
    setImportProgress,
    setSubscriptionVariables,
    setSubscriptionEnabled,
  } = importState;

  if (
    validatedContacts.valid.length === 0 &&
    validatedContacts.invalid.length === 0
  ) {
    // This is the first time loading the validation step, validate contacts
    validateContacts();
  }

  function validateContacts() {
    // Transform CSV data to contact inputs and validate
    const fieldMap: Record<string, keyof IContactInput> = {
      "First name": "firstName",
      "Last name": "lastName",
      "Job title": "title",
      "Contact LinkedIn URL": "contactLinkedinUrl",
      "Company name": "companyName",
      "Company website domain": "companyWebsiteDomain",
      "Company LinkedIn URL": "companyLinkedinUrl",
    };

    const valid: IContactInput[] = [];
    const invalid: IContactInput[] = [];
    const invalidReasons: Record<number, ContactInvalidReasonsEnum[]> = {};

    csvData.forEach((row, index) => {
      const contact: any = {
        originalRowIndex: index,
        rawData: { ...row },
      };

      // Map CSV fields to contact fields
      mappings.forEach((mapping) => {
        if (mapping.csvField && row[mapping.csvField] !== undefined) {
          const fieldName = fieldMap[mapping.ciroField];
          if (fieldName) {
            contact[fieldName] = row[mapping.csvField];
          }
        }
      });

      // Validate the contact
      let isValid = true;
      const contactInvalidReasons: ContactInvalidReasonsEnum[] = [];

      // Check if required fields have values
      if (!contact.firstName || contact.firstName.trim() === "") {
        isValid = false;
        contactInvalidReasons.push(
          ContactInvalidReasonsEnum.MISSING_FIRST_NAME,
        );
      }

      if (!contact.lastName || contact.lastName.trim() === "") {
        isValid = false;
        contactInvalidReasons.push(ContactInvalidReasonsEnum.MISSING_LAST_NAME);
      }

      // Check if at least one of Contact LinkedIn URL or Company Name is provided
      const hasContactLinkedinUrl =
        contact.contactLinkedinUrl && contact.contactLinkedinUrl.trim() !== "";
      const hasCompanyName =
        contact.companyName && contact.companyName.trim() !== "";

      if (!hasContactLinkedinUrl && !hasCompanyName) {
        isValid = false;
        contactInvalidReasons.push(
          ContactInvalidReasonsEnum.MISSING_CONTACT_LINKEDIN_OR_COMPANY,
        );
      }

      // Validate Company LinkedIn URL if present
      if (contact.companyLinkedinUrl) {
        const linkedInUrl = contact.companyLinkedinUrl.trim();
        if (linkedInUrl !== "") {
          // Check if it's a valid LinkedIn URL
          const linkedInPattern = /^(https?:\/\/)?(www\.)?linkedin\.com\/.+/i;
          if (!linkedInPattern.test(linkedInUrl)) {
            isValid = false;
            contactInvalidReasons.push(
              ContactInvalidReasonsEnum.INVALID_COMPANY_LINKEDIN_URL,
            );
          }
        }
      }

      // Validate Contact LinkedIn URL if present
      if (contact.contactLinkedinUrl) {
        const contactLinkedInUrl = contact.contactLinkedinUrl.trim();
        if (contactLinkedInUrl !== "") {
          // Check if it's a valid LinkedIn URL
          const linkedInPattern =
            /^(https?:\/\/)?(www\.)?linkedin\.com\/(in|pub)\/.+/i;
          if (!linkedInPattern.test(contactLinkedInUrl)) {
            isValid = false;
            contactInvalidReasons.push(
              ContactInvalidReasonsEnum.INVALID_CONTACT_LINKEDIN_URL,
            );
          }
        }
      }

      if (isValid) {
        valid.push(contact);
      } else {
        invalid.push(contact);
        invalidReasons[index] = contactInvalidReasons;
      }
    });

    setValidatedContacts({
      valid,
      invalid,
      invalidReasons,
    });

    return valid.length > 0;
  }

  const handleImport = () => {
    setIsProcessing(true);
    setStep(ImportStepEnum.PROCESSING);

    try {
      const allContacts = validatedContacts.valid.map((contact) => {
        const contactInput: IContactInput = {};

        if (contact.firstName) contactInput.firstName = contact.firstName;
        if (contact.lastName) contactInput.lastName = contact.lastName;
        if (contact.title) contactInput.title = contact.title;
        if (contact.companyName) contactInput.companyName = contact.companyName;
        if (contact.companyWebsiteDomain)
          contactInput.companyWebsiteDomain = contact.companyWebsiteDomain;
        if (contact.companyLinkedinUrl)
          contactInput.companyLinkedinUrl = contact.companyLinkedinUrl;
        if (contact.contactLinkedinUrl)
          contactInput.contactLinkedinUrl = contact.contactLinkedinUrl;

        return contactInput;
      });

      if (allContacts.length === 0) {
        setError("No valid contacts found in the CSV file");
        setStep(ImportStepEnum.MAPPING);
        setIsProcessing(false);
        return;
      }

      // Initialize progress state
      setImportProgress({
        id: crypto.randomUUID(),
        processedCount: 0,
        totalCount: allContacts.length,
        duplicateContacts: [],
        completedContacts: [],
        errors: [],
      });

      // Set subscription variables and enable the subscription
      try {
        setSubscriptionVariables({
          listId: contactListId,
          contacts: allContacts,
        });

        setSubscriptionEnabled(true);
      } catch (subscriptionError) {
        console.error("Subscription setup error:", subscriptionError);
        setError(
          "There was an error with the import. Contact LinkedIn URLs are collected but not yet supported for importing to the server.",
        );
        setIsProcessing(false);
        setStep(ImportStepEnum.VALIDATION);
      }
    } catch (err) {
      console.error("Error starting import:", err);
      setError(
        err instanceof Error ? err.message : "An unknown error occurred",
      );
      setStep(ImportStepEnum.VALIDATION);
      setIsProcessing(false);
    }
  };

  const totalRows =
    validatedContacts.valid.length + validatedContacts.invalid.length;
  const displayContacts = showOnlyInvalid
    ? validatedContacts.invalid
    : [...validatedContacts.valid, ...validatedContacts.invalid];

  return (
    <div className={classNames("p-6", "max-h-screen")}>
      <div className={classNames("flex", "items-center")}>
        <button
          onClick={() => {
            setValidatedContacts({
              valid: [],
              invalid: [],
              invalidReasons: {},
            });
            setStep(ImportStepEnum.MAPPING);
          }}
          className={classNames(
            "flex",
            "items-center",
            "text-gray-600",
            "hover:text-gray-900",
            "mr-4",
          )}
        >
          ← Back
        </button>
        <h2 className={classNames("text-xl", "font-medium", "flex-1")}>
          <span className={classNames("inline-flex", "items-center", "gap-2")}>
            <span
              className={classNames(
                "text-green-500",
                "rounded-full",
                "bg-green-50",
                "p-1",
              )}
            >
              <CheckIcon className="h-5 w-5" />
            </span>
            Validation complete
          </span>
        </h2>
      </div>

      <div
        className={classNames(
          "mt-4",
          "flex",
          "items-center",
          "justify-between",
        )}
      >
        <p className={classNames("text-gray-600")}>
          {totalRows} rows total: {validatedContacts.valid.length} valid |{" "}
          {validatedContacts.invalid.length} invalid
        </p>

        <label className={classNames("flex", "items-center", "cursor-pointer")}>
          <input
            type="checkbox"
            checked={showOnlyInvalid}
            onChange={() => setShowOnlyInvalid(!showOnlyInvalid)}
            className={classNames(
              "mr-2",
              "h-4",
              "w-4",
              "text-orange-500",
              "focus:ring-orange-400",
            )}
          />
          <span className={classNames("text-sm")}>Show invalid rows only</span>
        </label>
      </div>

      <div className={classNames("mt-4", "overflow-hidden")}>
        <div
          className={classNames(
            "max-h-96",
            "overflow-auto",
            "border",
            "border-gray-200",
            "rounded-md",
          )}
        >
          <CiroTable>
            <thead className="sticky top-0 bg-white">
              <CiroTableRow>
                <CiroTableHeader width="w-16">Row</CiroTableHeader>
                <CiroTableHeader width="w-32">First Name</CiroTableHeader>
                <CiroTableHeader width="w-32">Last Name</CiroTableHeader>
                <CiroTableHeader width="w-64">
                  Contact LinkedIn URL
                </CiroTableHeader>
                <CiroTableHeader width="w-64">Company</CiroTableHeader>
                <CiroTableHeader width="w-64">
                  Company LinkedIn URL
                </CiroTableHeader>
              </CiroTableRow>
            </thead>
            <tbody>
              {displayContacts.map((contact) => {
                const isValid = validatedContacts.valid.includes(contact);
                const rowIndex = contact.originalRowIndex;
                const invalidReason =
                  validatedContacts.invalidReasons[rowIndex];

                return (
                  <CiroTableRow
                    key={rowIndex}
                    className={isValid ? "" : "bg-gray-50"}
                    title={
                      !isValid
                        ? `Validation errors: ${invalidReason?.join(", ")}`
                        : undefined
                    }
                  >
                    <CiroTableCell>
                      {!isValid ? (
                        <span
                          className={classNames(
                            "text-orange-500",
                            "flex",
                            "items-center",
                            "gap-1",
                          )}
                        >
                          {rowIndex + 1}
                          <span
                            className={classNames(
                              "inline-block",
                              "rounded-full",
                              "bg-orange-100",
                              "p-1",
                            )}
                          >
                            <XCircleIcon className="h-3 w-3" />
                          </span>
                        </span>
                      ) : (
                        rowIndex + 1
                      )}
                    </CiroTableCell>
                    <CiroTableCell>
                      {!isValid &&
                      invalidReason?.includes(
                        ContactInvalidReasonsEnum.MISSING_FIRST_NAME,
                      ) ? (
                        <CiroTooltipContainer
                          tooltip={<div>First name is required</div>}
                        >
                          <span
                            className={classNames(
                              "text-orange-500",
                              "flex",
                              "items-center",
                              "gap-1",
                            )}
                          >
                            <span
                              className={classNames(
                                "inline-block",
                                "rounded-full",
                                "bg-orange-100",
                                "p-1",
                                "flex-shrink-0",
                              )}
                            >
                              <XCircleIcon className="h-3 w-3" />
                            </span>
                            <span className="truncate">
                              {contact.firstName || "Missing"}
                            </span>
                          </span>
                        </CiroTooltipContainer>
                      ) : (
                        <div
                          className="truncate max-w-xs"
                          title={contact.firstName || ""}
                        >
                          {contact.firstName || "-"}
                        </div>
                      )}
                    </CiroTableCell>
                    <CiroTableCell>
                      {!isValid &&
                      invalidReason?.includes(
                        ContactInvalidReasonsEnum.MISSING_LAST_NAME,
                      ) ? (
                        <CiroTooltipContainer
                          tooltip={<div>Last name is required</div>}
                        >
                          <span
                            className={classNames(
                              "text-orange-500",
                              "flex",
                              "items-center",
                              "gap-1",
                            )}
                          >
                            <span
                              className={classNames(
                                "inline-block",
                                "rounded-full",
                                "bg-orange-100",
                                "p-1",
                                "flex-shrink-0",
                              )}
                            >
                              <XCircleIcon className="h-3 w-3" />
                            </span>
                            <span className="truncate">
                              {contact.lastName || "Missing"}
                            </span>
                          </span>
                        </CiroTooltipContainer>
                      ) : (
                        <div
                          className="truncate max-w-xs"
                          title={contact.lastName || ""}
                        >
                          {contact.lastName || "-"}
                        </div>
                      )}
                    </CiroTableCell>
                    <CiroTableCell>
                      {!isValid &&
                      (invalidReason?.includes(
                        ContactInvalidReasonsEnum.INVALID_CONTACT_LINKEDIN_URL,
                      ) ||
                        (invalidReason?.includes(
                          ContactInvalidReasonsEnum.MISSING_CONTACT_LINKEDIN_OR_COMPANY,
                        ) &&
                          !contact.companyName)) ? (
                        <CiroTooltipContainer
                          tooltip={
                            <div>
                              {invalidReason?.includes(
                                ContactInvalidReasonsEnum.INVALID_CONTACT_LINKEDIN_URL,
                              )
                                ? "Invalid LinkedIn URL"
                                : "Required with no company name"}
                            </div>
                          }
                        >
                          <span
                            className={classNames(
                              "text-orange-500",
                              "flex",
                              "items-center",
                              "gap-1",
                            )}
                          >
                            <span
                              className={classNames(
                                "inline-block",
                                "rounded-full",
                                "bg-orange-100",
                                "p-1",
                                "flex-shrink-0",
                              )}
                            >
                              <XCircleIcon className="h-3 w-3" />
                            </span>
                            <span className="truncate">
                              {contact.contactLinkedinUrl}
                            </span>
                          </span>
                        </CiroTooltipContainer>
                      ) : (
                        <div
                          className="truncate max-w-xs"
                          title={contact.contactLinkedinUrl || ""}
                        >
                          {contact.contactLinkedinUrl ?? "-"}
                        </div>
                      )}
                    </CiroTableCell>
                    <CiroTableCell>
                      {!isValid &&
                      (invalidReason?.includes(
                        ContactInvalidReasonsEnum.MISSING_COMPANY_NAME,
                      ) ||
                        (invalidReason?.includes(
                          ContactInvalidReasonsEnum.MISSING_CONTACT_LINKEDIN_OR_COMPANY,
                        ) &&
                          !contact.contactLinkedinUrl)) ? (
                        <CiroTooltipContainer
                          tooltip={
                            <div>
                              {invalidReason?.includes(
                                ContactInvalidReasonsEnum.MISSING_CONTACT_LINKEDIN_OR_COMPANY,
                              )
                                ? "Required when no LinkedIn URL provided"
                                : "Company name is required"}
                            </div>
                          }
                        >
                          <span
                            className={classNames(
                              "text-orange-500",
                              "flex",
                              "items-center",
                              "gap-1",
                            )}
                          >
                            <span
                              className={classNames(
                                "inline-block",
                                "rounded-full",
                                "bg-orange-100",
                                "p-1",
                                "flex-shrink-0",
                              )}
                            >
                              <XCircleIcon className="h-3 w-3" />
                            </span>
                            <span className="truncate">
                              {contact.companyName || "Missing"}
                            </span>
                          </span>
                        </CiroTooltipContainer>
                      ) : (
                        <div
                          className="truncate max-w-xs"
                          title={contact.companyName || ""}
                        >
                          {contact.companyName || "-"}
                        </div>
                      )}
                    </CiroTableCell>
                    <CiroTableCell>
                      {!isValid &&
                      invalidReason?.includes(
                        ContactInvalidReasonsEnum.INVALID_COMPANY_LINKEDIN_URL,
                      ) ? (
                        <CiroTooltipContainer
                          tooltip={<div>Invalid LinkedIn URL</div>}
                        >
                          <span
                            className={classNames(
                              "text-orange-500",
                              "flex",
                              "items-center",
                              "gap-1",
                            )}
                          >
                            <span
                              className={classNames(
                                "inline-block",
                                "rounded-full",
                                "bg-orange-100",
                                "p-1",
                                "flex-shrink-0",
                              )}
                            >
                              <XCircleIcon className="h-3 w-3" />
                            </span>
                            <span className="truncate">
                              {contact.companyLinkedinUrl}
                            </span>
                          </span>
                        </CiroTooltipContainer>
                      ) : (
                        <div
                          className="truncate max-w-xs"
                          title={contact.companyLinkedinUrl || ""}
                        >
                          {contact.companyLinkedinUrl ?? "-"}
                        </div>
                      )}
                    </CiroTableCell>
                  </CiroTableRow>
                );
              })}
            </tbody>
          </CiroTable>
        </div>
      </div>

      <div className={classNames("mt-6")}>
        <CiroButton
          analyticsField={"Import valid contacts"}
          onClick={handleImport}
          style={CiroButtonStyleEnum.LOUD}
          disabled={isProcessing || validatedContacts.valid.length === 0}
          customClassName={classNames("w-full")}
        >
          Import Valid Rows
        </CiroButton>
      </div>
    </div>
  );
};

export default ValidationStep;
